/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useConfig } from '@modules/cui/ConfigContext'
import { useGetServerlessTrafficFilterQuery } from '@modules/network-security-lib/hooks/serverless'
import { AnyUserProject } from '@modules/project-user-api/types'

export const useProjectHasTrafficFilters = (project?: AnyUserProject) => {
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  const trafficFilterIds = isAdminConsole ? [] : project?.traffic_filters?.map(({ id }) => id) || []
  const result = useGetServerlessTrafficFilterQuery({ ids: trafficFilterIds })

  const isLoading = result.some(({ isLoading }) => isLoading)
  const isFetching = result.some(({ isFetching }) => isFetching)
  const isError = result.some(({ isError }) => isError)

  const hasPrivateLink = result.some(({ data }) => {
    if (!trafficFilterIds.length) {
      return false
    }

    if (!data || isLoading || isFetching || isError) {
      return false
    }

    if (data.type !== 'vpce') {
      return false
    }

    if (!data.rules?.length) {
      return false
    }

    return true
  })

  const hasIpFilter = result.some(({ data }) => {
    if (!trafficFilterIds.length) {
      return false
    }

    if (!data || isLoading || isFetching || isError) {
      return false
    }

    if (data.type !== 'ip') {
      return false
    }

    if (!data.rules?.length) {
      return false
    }

    return true
  })

  return {
    hasPrivateLink,
    hasIpFilter,
    isFetching,
    isLoading,
  }
}

// Whether or not we should use private endpoints for the project is based on the following rules:
// - If the project has a private link traffic filter and no IP traffic filter, we should use private endpoints
// - If the project has an IP traffic filter and no private link traffic filter, we should use public endpoints
// - If the project has both a private link traffic filter and an IP traffic filter, we should use public endpoints
// - If the project has no traffic filters, we should use public endpoints

export const getShouldUsePrivateEndpoints = ({
  hasPrivateLink,
  hasIpFilter,
}: {
  hasPrivateLink: boolean
  hasIpFilter: boolean
}) => {
  if (hasPrivateLink && !hasIpFilter) {
    return true
  }

  return false
}
